import { useState } from "react";
import { useParams, Navigate } from "react-router";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/react";
import PageMeta from "@/components/seo/PageMeta";
import { getRetreatBySlug } from "@/data/content";
import type { UpcomingRetreat, ReservationContent, ReservationFieldContent } from "@/data/content";
import { useContent } from "@/hooks/useContent";
import ScriptText from "@/components/ui/ScriptText";
import Divider from "@/components/ui/Divider";
import Button from "@/components/ui/Button";

interface ReservationFormData {
  fullName: string;
  email: string;
  phone: string;
  travelingFrom: string;
  socialHandles: string;
  callingYou: string;
  visitedBefore: string;
  visitedBeforeDetails: string;
  hopingToExperience: string;
  nourishingEnvironments: string;
  previousRetreat: string;
  previousRetreatDetails: string;
  travelingSolo: string;
  howHeard: string;
  dietary: string;
  passport: string;
  physicalActivity: string;
  groupSetting: string;
  ack0: boolean;
  ack1: boolean;
  ack2: boolean;
  ack3: boolean;
  ack4: boolean;
  ack5: boolean;
  ack6: boolean;
  ack7: boolean;
  ack8: boolean;
  aboutYou: string;
  additionalQuestions: string;
  website: string;
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const labelClasses =
  "block mb-2 font-body text-sm tracking-wide text-soft-charcoal/70";
const inputClasses =
  "w-full border-0 border-b border-warm-stone/40 bg-transparent px-0 py-4 font-body text-lg text-soft-charcoal placeholder:text-warm-stone/60 focus:border-faded-terracotta focus:outline-none transition-colors duration-300";
const radioGroupClasses =
  "flex gap-6 pt-3";
const radioLabelClasses =
  "flex cursor-pointer items-center gap-2 font-body text-lg text-soft-charcoal";
const radioClasses =
  "h-4 w-4 appearance-none rounded-full border border-warm-stone/40 bg-transparent checked:border-faded-terracotta checked:bg-faded-terracotta transition-colors duration-300 cursor-pointer";

function SectionHeader({ scriptAccent, heading }: { scriptAccent: string; heading: string }) {
  return (
    <div className="text-center">
      <ScriptText className="mb-2 block text-xl lg:text-2xl">
        {scriptAccent}
      </ScriptText>
      <h3 className="font-serif text-xl font-light tracking-wide text-soft-charcoal lg:text-2xl">
        {heading}
      </h3>
      <span className="mt-3 inline-block h-px w-12 bg-muted-gold/30" />
    </div>
  );
}

function FieldNote({ note }: { note?: string }) {
  if (!note) return null;
  return (
    <p className="mt-2 font-body text-xs leading-relaxed text-soft-charcoal/80 italic">
      {note}
    </p>
  );
}

function TextField({
  id,
  field,
  register,
  error,
  required,
  maxLength,
  type = "text",
}: {
  id: string;
  field: ReservationFieldContent;
  register: ReturnType<typeof useForm<ReservationFormData>>["register"];
  error?: string;
  required?: boolean;
  maxLength?: number;
  type?: "text" | "email" | "tel";
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClasses}>
        {field.label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={field.placeholder}
        maxLength={maxLength}
        className={inputClasses}
        {...register(id as keyof ReservationFormData, required ? {
          required: `This field is required`,
          ...(type === "email" ? {
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address",
            },
          } : {}),
        } : undefined)}
      />
      <FieldNote note={field.note} />
      {error && (
        <p className="mt-2 text-xs text-faded-terracotta">{error}</p>
      )}
    </div>
  );
}

function CharCount({ value, max }: { value: string | undefined; max: number }) {
  const len = value?.length ?? 0;
  const ratio = len / max;
  const color =
    ratio >= 1
      ? "text-faded-terracotta"
      : ratio >= 0.85
        ? "text-faded-terracotta/70"
        : "text-warm-stone/40";
  return (
    <p className={`mt-2 text-right font-body text-xs tabular-nums transition-colors duration-300 ${color}`}>
      {len} / {max}
    </p>
  );
}

function TextAreaField({
  id,
  field,
  register,
  watch,
  rows = 3,
  maxLength,
}: {
  id: string;
  field: ReservationFieldContent;
  register: ReturnType<typeof useForm<ReservationFormData>>["register"];
  watch: ReturnType<typeof useForm<ReservationFormData>>["watch"];
  rows?: number;
  maxLength?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClasses}>
        {field.label}
      </label>
      <textarea
        id={id}
        rows={rows}
        placeholder={field.placeholder}
        maxLength={maxLength}
        className={`${inputClasses} resize-none`}
        {...register(id as keyof ReservationFormData)}
      />
      <FieldNote note={field.note} />
      {maxLength && <CharCount value={watch(id as keyof ReservationFormData) as string} max={maxLength} />}
    </div>
  );
}

function RadioWithDetails({
  id,
  detailsId,
  field,
  register,
  watch,
  detailsPlaceholder,
  maxLength,
}: {
  id: string;
  detailsId: string;
  field: ReservationFieldContent;
  register: ReturnType<typeof useForm<ReservationFormData>>["register"];
  watch: ReturnType<typeof useForm<ReservationFormData>>["watch"];
  detailsPlaceholder?: string;
  maxLength?: number;
}) {
  const value = watch(id as keyof ReservationFormData);
  return (
    <div>
      <label className={labelClasses}>{field.label}</label>
      <div className={radioGroupClasses}>
        <label className={radioLabelClasses}>
          <input
            type="radio"
            value="Yes"
            className={radioClasses}
            {...register(id as keyof ReservationFormData)}
          />
          Yes
        </label>
        <label className={radioLabelClasses}>
          <input
            type="radio"
            value="No"
            className={radioClasses}
            {...register(id as keyof ReservationFormData)}
          />
          No
        </label>
      </div>
      {value === "Yes" && (
        <textarea
          id={detailsId}
          rows={2}
          placeholder={detailsPlaceholder || field.placeholder}
          maxLength={maxLength}
          className={`${inputClasses} mt-4 resize-none`}
          {...register(detailsId as keyof ReservationFormData)}
        />
      )}
      <FieldNote note={field.note} />
    </div>
  );
}

function RadioField({
  id,
  field,
  register,
}: {
  id: string;
  field: ReservationFieldContent;
  register: ReturnType<typeof useForm<ReservationFormData>>["register"];
}) {
  return (
    <div>
      <label className={labelClasses}>{field.label}</label>
      <div className={radioGroupClasses}>
        <label className={radioLabelClasses}>
          <input
            type="radio"
            value="Yes"
            className={radioClasses}
            {...register(id as keyof ReservationFormData)}
          />
          Yes
        </label>
        <label className={radioLabelClasses}>
          <input
            type="radio"
            value="No"
            className={radioClasses}
            {...register(id as keyof ReservationFormData)}
          />
          No
        </label>
      </div>
      <FieldNote note={field.note} />
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailField({
  field,
  register,
  watch,
  error,
}: {
  field: ReservationFieldContent;
  register: ReturnType<typeof useForm<ReservationFormData>>["register"];
  watch: ReturnType<typeof useForm<ReservationFormData>>["watch"];
  error?: string;
}) {
  const value = watch("email") ?? "";
  const showHint = value.length > 0 && !EMAIL_RE.test(value);

  return (
    <div>
      <label htmlFor="email" className={labelClasses}>
        {field.label}
      </label>
      <input
        id="email"
        type="email"
        placeholder={field.placeholder}
        maxLength={254}
        className={inputClasses}
        {...register("email", {
          required: "This field is required",
          pattern: {
            value: EMAIL_RE,
            message: "Please enter a valid email address",
          },
        })}
      />
      {showHint && (
        <p className="mt-2 font-body text-xs leading-relaxed text-soft-charcoal/80 italic">
          Please enter a valid email address
        </p>
      )}
      {error && !showHint && (
        <p className="mt-2 text-xs text-faded-terracotta">{error}</p>
      )}
    </div>
  );
}

export default function ReservationPage() {
  const { retreatSlug } = useParams();
  const staticRetreat = retreatSlug ? getRetreatBySlug(retreatSlug) : undefined;
  const retreat = useContent(`retreats/${retreatSlug ?? ""}`, staticRetreat);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ReservationFormData>({ shouldFocusError: false });

  const [status, setStatus] = useState<SubmitStatus>("idle");

  if (
    !retreat ||
    retreat.status === "coming-soon" ||
    (retreat as UpcomingRetreat).phase !== "reservation" ||
    !(retreat as UpcomingRetreat).reservation
  ) {
    return <Navigate to={retreatSlug ? `/retreats/${retreatSlug}` : "/retreats"} replace />;
  }

  const r = retreat as UpcomingRetreat;
  const content = r.reservation as ReservationContent;
  const s = content.sections;

  const onSubmit = async (data: ReservationFormData) => {
    setStatus("submitting");
    try {
      const visitedBeforeCombined =
        data.visitedBefore === "Yes"
          ? `Yes — ${data.visitedBeforeDetails || ""}`.trim()
          : data.visitedBefore || "";
      const previousRetreatCombined =
        data.previousRetreat === "Yes"
          ? `Yes — ${data.previousRetreatDetails || ""}`.trim()
          : data.previousRetreat || "";

      const res = await fetch("/api/reservation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: data.fullName,
          email: data.email,
          phone: data.phone,
          travelingFrom: data.travelingFrom,
          socialHandles: data.socialHandles,
          callingYou: data.callingYou,
          visitedBefore: visitedBeforeCombined,
          hopingToExperience: data.hopingToExperience,
          nourishingEnvironments: data.nourishingEnvironments,
          previousRetreat: previousRetreatCombined,
          travelingSolo: data.travelingSolo,
          howHeard: data.howHeard,
          dietary: data.dietary,
          passport: data.passport,
          physicalActivity: data.physicalActivity,
          groupSetting: data.groupSetting,
          aboutYou: data.aboutYou,
          additionalQuestions: data.additionalQuestions,
          retreatSlug: r.slug,
          retreatTitle: r.title,
          region: r.region,
          acknowledgementsAccepted: true,
          website: data.website,
        }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setStatus("success");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const err = new Error(result.error || "Reservation form submission failed");
        Sentry.captureException(err, { tags: { form: "reservation" } });
        setStatus("error");
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { form: "reservation" } });
      setStatus("error");
    }
  };

  const hasAckErrors = Object.keys(errors).some((k) => k.startsWith("ack"));

  return (
    <>
      <PageMeta
        title={`Reserve Your Place — ${r.title} | Chasing Provence`}
        description={`Reservation questionnaire for ${r.title}, an intimate women's retreat in ${r.location}. ${r.season}.`}
        image={r.images.hero || undefined}
      />

      <div className="min-h-screen bg-parchment px-6 pt-32 pb-20 lg:px-12 lg:pt-40 lg:pb-32">
        <div className="mx-auto max-w-2xl">
          {/* Preface */}
          <div className="mb-16 text-center">
            <ScriptText className="mb-4 block text-2xl lg:text-3xl">
              {content.preface.scriptAccent}
            </ScriptText>
            <h1 className="mb-6 font-serif font-light text-soft-charcoal">
              {content.preface.heading}
            </h1>
            <p className="font-body text-lg leading-relaxed text-soft-charcoal/80">
              {content.preface.body}
            </p>
          </div>

          {status === "success" ? (
            <div className="rounded-sm bg-cream/50 p-8 lg:p-12">
              <div className="py-12 text-center">
                <ScriptText className="mb-4 block text-3xl lg:text-4xl">
                  {content.successScript}
                </ScriptText>
                <p className="mx-auto max-w-md font-body leading-relaxed text-soft-charcoal/80">
                  {content.successBody}
                </p>

                <Divider />

                <ScriptText className="mb-2 block text-xl lg:text-2xl">
                  {s.nextSteps.scriptAccent}
                </ScriptText>
                <h3 className="mb-4 font-serif text-xl font-light tracking-wide text-soft-charcoal lg:text-2xl">
                  {s.nextSteps.heading}
                </h3>
                <p className="mx-auto max-w-md font-body leading-relaxed text-soft-charcoal/70">
                  {s.nextSteps.body}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-sm bg-cream/50 p-8 lg:p-12">
              <form onSubmit={handleSubmit(onSubmit, (fieldErrors) => {
                const keys = Object.keys(fieldErrors);
                if (!keys.length) return;
                const selector = keys.map((k) => `[name="${k}"]`).join(",");
                const all = Array.from(document.querySelectorAll<HTMLElement>(selector));
                if (all.length) {
                  all[0].scrollIntoView({ behavior: "smooth", block: "center" });
                  all[0].focus({ preventScroll: true });
                }
              })} className="space-y-10" noValidate>
                {/* Honeypot */}
                <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    {...register("website")}
                  />
                </div>

                {/* ── Your Details ── */}
                <SectionHeader scriptAccent={s.yourDetails.scriptAccent} heading={s.yourDetails.heading} />
                <div className="space-y-8">
                  <TextField id="fullName" field={s.yourDetails.fields!.fullName} register={register} error={errors.fullName?.message} required maxLength={200} />
                  <EmailField field={s.yourDetails.fields!.email} register={register} watch={watch} error={errors.email?.message} />
                  <TextField id="phone" field={s.yourDetails.fields!.phone} register={register} maxLength={30} type="tel" />
                  <TextField id="travelingFrom" field={s.yourDetails.fields!.travelingFrom} register={register} maxLength={300} />
                  <TextField id="socialHandles" field={s.yourDetails.fields!.socialHandles} register={register} maxLength={300} />
                </div>

                <Divider />

                {/* ── Your Story ── */}
                <SectionHeader scriptAccent={s.yourStory.scriptAccent} heading={s.yourStory.heading} />
                <div className="space-y-8">
                  <TextAreaField id="callingYou" field={s.yourStory.fields!.callingYou} register={register} watch={watch} maxLength={2000} />
                  <RadioWithDetails
                    id="visitedBefore"
                    detailsId="visitedBeforeDetails"
                    field={s.yourStory.fields!.visitedBefore}
                    register={register}
                    watch={watch}
                    detailsPlaceholder="Share a memory or impression, if you'd like"
                    maxLength={2000}
                  />
                  <TextAreaField id="hopingToExperience" field={s.yourStory.fields!.hopingToExperience} register={register} watch={watch} maxLength={2000} />
                  <TextField id="nourishingEnvironments" field={s.yourStory.fields!.nourishingEnvironments} register={register} maxLength={500} />
                  <RadioWithDetails
                    id="previousRetreat"
                    detailsId="previousRetreatDetails"
                    field={s.yourStory.fields!.previousRetreat}
                    register={register}
                    watch={watch}
                    detailsPlaceholder="Share a bit about the experience, if you'd like"
                    maxLength={500}
                  />
                </div>

                <Divider />

                {/* ── Travel & Companions ── */}
                <SectionHeader scriptAccent={s.travelCompanions.scriptAccent} heading={s.travelCompanions.heading} />
                <div className="space-y-8">
                  <TextField id="travelingSolo" field={s.travelCompanions.fields!.travelingSolo} register={register} maxLength={500} />
                  <TextField id="howHeard" field={s.travelCompanions.fields!.howHeard} register={register} maxLength={500} />
                </div>

                <Divider />

                {/* ── Health & Activities ── */}
                <SectionHeader scriptAccent={s.healthActivities.scriptAccent} heading={s.healthActivities.heading} />
                <div className="space-y-8">
                  <TextAreaField id="dietary" field={s.healthActivities.fields!.dietary} register={register} watch={watch} maxLength={1000} />
                  <RadioField id="passport" field={s.healthActivities.fields!.passport} register={register} />
                  <RadioField id="physicalActivity" field={s.healthActivities.fields!.physicalActivity} register={register} />
                  <RadioField id="groupSetting" field={s.healthActivities.fields!.groupSetting} register={register} />
                </div>

                <Divider />

                {/* ── Acknowledgements ── */}
                <SectionHeader scriptAccent={s.acknowledgements.scriptAccent} heading={s.acknowledgements.heading} />
                {s.acknowledgements.intro && (
                  <p className="text-center font-body text-sm leading-relaxed text-soft-charcoal/60">
                    {s.acknowledgements.intro}
                  </p>
                )}
                <div className="space-y-5">
                  {s.acknowledgements.items!.map((item, i) => (
                    <label key={i} className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 shrink-0 appearance-none rounded-sm border border-warm-stone/40 bg-transparent checked:border-faded-terracotta checked:bg-faded-terracotta transition-colors duration-300 cursor-pointer"
                        {...register(`ack${i}` as keyof ReservationFormData, { required: true })}
                      />
                      <span className="font-body text-sm leading-relaxed text-soft-charcoal/70">
                        {item}
                      </span>
                    </label>
                  ))}
                </div>
                {hasAckErrors && (
                  <p className="text-center text-xs text-faded-terracotta">
                    Please confirm all acknowledgements to continue.
                  </p>
                )}

                <Divider />

                {/* ── Anything Else ── */}
                <SectionHeader scriptAccent={s.anythingElse.scriptAccent} heading={s.anythingElse.heading} />
                <div className="space-y-8">
                  <TextAreaField id="aboutYou" field={s.anythingElse.fields!.aboutYou} register={register} watch={watch} rows={4} maxLength={2000} />
                  <TextAreaField id="additionalQuestions" field={s.anythingElse.fields!.additionalQuestions} register={register} watch={watch} rows={4} maxLength={2000} />
                </div>

                {/* Error message */}
                {status === "error" && (
                  <p className="text-center text-sm text-faded-terracotta">
                    {content.errorText}
                  </p>
                )}

                {/* Submit */}
                <div className="pt-4 text-center">
                  <Button
                    type="submit"
                    variant="filled"
                    disabled={status === "submitting"}
                  >
                    {status === "submitting" ? content.submittingText : content.submitText}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
